import React, { useEffect, useRef, useState } from 'react';
import { X, UtensilsCrossed, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from 'lucide-react';
import { apiService } from '@/services/api';

interface MenuImage {
  id: string;
  imageUrl: string;
  caption: string | null;
  sortOrder: number;
  isActive: boolean;
}

interface Props {
  branchId: string;
  /** Optional venue name shown in the header. */
  branchName?: string;
  onClose: () => void;
}

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const dist = (a: React.Touch | Touch, b: React.Touch | Touch) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

/**
 * Menu viewer for seated customers (registered + QR guests). Shows the branch's uploaded menu
 * photos. Tap a photo to open a fullscreen viewer with prev/next navigation and zoom
 * (pinch, double-tap, and +/- controls, with drag-to-pan while zoomed).
 */
const MenuImagesModal: React.FC<Props> = ({ branchId, branchName, onClose }) => {
  const [images, setImages] = useState<MenuImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fullscreen viewer state.
  const [index, setIndex] = useState<number | null>(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);

  // Gesture bookkeeping (refs so handlers don't churn state).
  const g = useRef({
    mode: 'none' as 'none' | 'pan' | 'pinch',
    startDist: 0,
    startScale: 1,
    startX: 0,
    startY: 0,
    startOffset: { x: 0, y: 0 },
    lastTap: 0,
    moved: false,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = (await apiService.getBranchMenuImages(branchId)) as any;
        // The endpoint returns { success, images } — images is top-level, not nested under data.
        const list: MenuImage[] = (res?.images as MenuImage[]) || (res?.data?.images as MenuImage[]) || [];
        if (!cancelled) setImages(list);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load the menu.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [branchId]);

  const resetZoom = () => { setScale(1); setOffset({ x: 0, y: 0 }); };
  const openViewer = (i: number) => { setIndex(i); resetZoom(); };
  const closeViewer = () => { setIndex(null); resetZoom(); };

  const go = (delta: number) => {
    setIndex((i) => {
      if (i === null) return i;
      const n = i + delta;
      if (n < 0 || n >= images.length) return i;
      return n;
    });
    resetZoom();
  };

  const zoomBy = (factor: number) => setScale((s) => clamp(+(s * factor).toFixed(2), MIN_SCALE, MAX_SCALE));

  // Keyboard nav (desktop).
  useEffect(() => {
    if (index === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'Escape') closeViewer();
      else if (e.key === '+' || e.key === '=') zoomBy(1.25);
      else if (e.key === '-') zoomBy(0.8);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, images.length]);

  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches;
    g.current.moved = false;
    if (t.length === 2) {
      g.current.mode = 'pinch';
      g.current.startDist = dist(t[0], t[1]);
      g.current.startScale = scale;
    } else if (t.length === 1) {
      const now = Date.now();
      if (now - g.current.lastTap < 300) {
        // Double-tap: toggle between fit and 2.5x.
        setScale((s) => (s > 1 ? 1 : 2.5));
        setOffset({ x: 0, y: 0 });
        g.current.lastTap = 0;
        return;
      }
      g.current.lastTap = now;
      g.current.mode = 'pan';
      g.current.startX = t[0].clientX;
      g.current.startY = t[0].clientY;
      g.current.startOffset = { ...offset };
      setDragging(true);
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    const t = e.touches;
    if (g.current.mode === 'pinch' && t.length === 2) {
      const ratio = dist(t[0], t[1]) / (g.current.startDist || 1);
      setScale(clamp(+(g.current.startScale * ratio).toFixed(2), MIN_SCALE, MAX_SCALE));
    } else if (g.current.mode === 'pan' && t.length === 1) {
      const dx = t[0].clientX - g.current.startX;
      const dy = t[0].clientY - g.current.startY;
      if (Math.abs(dx) > 6 || Math.abs(dy) > 6) g.current.moved = true;
      if (scale > 1) setOffset({ x: g.current.startOffset.x + dx, y: g.current.startOffset.y + dy });
    }
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    // Swipe left/right to change page when not zoomed.
    if (g.current.mode === 'pan' && scale === 1 && g.current.moved) {
      const dx = (e.changedTouches[0]?.clientX ?? 0) - g.current.startX;
      const dy = (e.changedTouches[0]?.clientY ?? 0) - g.current.startY;
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? 1 : -1);
    }
    if (e.touches.length === 0) { g.current.mode = 'none'; setDragging(false); }
  };

  const current = index !== null ? images[index] : null;

  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <div style={header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <UtensilsCrossed size={18} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '1rem' }}>Menu</div>
              <div style={subLabel}>{branchName || 'Browse, then order in chat'}</div>
            </div>
          </div>
          <button type="button" onClick={onClose} style={iconBtn} aria-label="Close"><X size={20} /></button>
        </div>

        <div style={body}>
          {loading ? (
            <p style={centerNote}>Loading menu…</p>
          ) : error ? (
            <p style={{ ...centerNote, color: '#dc2626' }}>{error}</p>
          ) : images.length === 0 ? (
            <p style={centerNote}>The venue hasn’t added a menu yet. You can still ask staff in chat.</p>
          ) : (
            <div style={grid}>
              {images.map((img, i) => (
                <button key={img.id} type="button" style={card} onClick={() => openViewer(i)}>
                  <img src={img.imageUrl} alt={img.caption || 'Menu page'} style={thumb} loading="lazy" />
                  <span style={zoomHint}><ZoomIn size={14} /></span>
                  {img.caption && <span style={caption}>{img.caption}</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={footerNote}>Found what you want? Send your order in the table chat.</div>
      </div>

      {current && (
        <div style={zoomOverlay} onClick={closeViewer}>
          {/* Top bar: page counter + close */}
          <div style={viewerTop} onClick={(e) => e.stopPropagation()}>
            <span style={counter}>{(index ?? 0) + 1} / {images.length}</span>
            <button type="button" onClick={closeViewer} style={viewerIconBtn} aria-label="Close"><X size={22} /></button>
          </div>

          {/* Image stage: pinch / double-tap / drag */}
          <div
            style={{ ...viewerStage, touchAction: 'none' }}
            onClick={(e) => e.stopPropagation()}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onDoubleClick={() => { setScale((s) => (s > 1 ? 1 : 2.5)); setOffset({ x: 0, y: 0 }); }}
          >
            <img
              src={current.imageUrl}
              alt={current.caption || 'Menu page'}
              draggable={false}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                transition: dragging ? 'none' : 'transform 0.15s ease',
                cursor: scale > 1 ? 'grab' : 'zoom-in',
                userSelect: 'none',
              }}
            />
          </div>

          {/* Prev / Next */}
          {index !== null && index > 0 && (
            <button type="button" style={{ ...navBtn, left: 10 }} onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label="Previous"><ChevronLeft size={26} /></button>
          )}
          {index !== null && index < images.length - 1 && (
            <button type="button" style={{ ...navBtn, right: 10 }} onClick={(e) => { e.stopPropagation(); go(1); }} aria-label="Next"><ChevronRight size={26} /></button>
          )}

          {current.caption && <div style={viewerCaption} onClick={(e) => e.stopPropagation()}>{current.caption}</div>}

          {/* Zoom controls */}
          <div style={zoomBar} onClick={(e) => e.stopPropagation()}>
            <button type="button" style={viewerIconBtn} onClick={() => zoomBy(0.8)} disabled={scale <= MIN_SCALE} aria-label="Zoom out"><ZoomOut size={20} /></button>
            <button type="button" style={{ ...viewerIconBtn, minWidth: 54, fontSize: '0.8rem', fontWeight: 700 }} onClick={resetZoom} aria-label="Reset zoom">{Math.round(scale * 100)}%</button>
            <button type="button" style={viewerIconBtn} onClick={() => zoomBy(1.25)} disabled={scale >= MAX_SCALE} aria-label="Zoom in"><ZoomIn size={20} /></button>
          </div>
        </div>
      )}
    </div>
  );
};

const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1300 };
const panel: React.CSSProperties = { background: '#fff', borderTopLeftRadius: 16, borderTopRightRadius: 16, width: 'min(520px, 100vw)', height: 'min(720px, 92vh)', display: 'flex', flexDirection: 'column', boxShadow: '0 -6px 30px rgba(0,0,0,0.25)' };
const header: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid #e5e7eb', gap: 8 };
const subLabel: React.CSSProperties = { fontSize: '0.72rem', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: '#374151', cursor: 'pointer', padding: 4, display: 'inline-flex' };
const body: React.CSSProperties = { flex: 1, overflowY: 'auto', padding: '12px 14px', background: '#f9fafb' };
const centerNote: React.CSSProperties = { textAlign: 'center', color: '#6b7280', padding: '2rem 1rem', fontSize: '0.9rem' };
const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 };
const card: React.CSSProperties = { position: 'relative', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', background: '#fff', padding: 0, cursor: 'zoom-in' };
const thumb: React.CSSProperties = { width: '100%', aspectRatio: '3 / 4', objectFit: 'cover', display: 'block' };
const zoomHint: React.CSSProperties = { position: 'absolute', top: 6, right: 6, background: 'rgba(15,23,42,0.65)', color: '#fff', borderRadius: 9999, width: 24, height: 24, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };
const caption: React.CSSProperties = { display: 'block', padding: '6px 8px', fontSize: '0.75rem', color: '#334155', textAlign: 'left', borderTop: '1px solid #f1f5f9' };
const footerNote: React.CSSProperties = { padding: '10px 14px', borderTop: '1px solid #e5e7eb', color: '#6b7280', fontSize: '0.8rem', textAlign: 'center' };

// Fullscreen viewer
const zoomOverlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1400, overflow: 'hidden' };
const viewerTop: React.CSSProperties = { position: 'absolute', top: 0, left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', zIndex: 2 };
const counter: React.CSSProperties = { color: '#fff', fontSize: '0.85rem', fontWeight: 600, background: 'rgba(255,255,255,0.12)', padding: '4px 10px', borderRadius: 9999 };
const viewerStage: React.CSSProperties = { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 };
const viewerIconBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.14)', border: 'none', color: '#fff', cursor: 'pointer', width: 40, height: 40, borderRadius: 9999 };
const navBtn: React.CSSProperties = { position: 'absolute', top: '50%', transform: 'translateY(-50%)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.14)', border: 'none', color: '#fff', cursor: 'pointer', width: 44, height: 44, borderRadius: 9999, zIndex: 2 };
const viewerCaption: React.CSSProperties = { position: 'absolute', bottom: 74, left: 0, right: 0, textAlign: 'center', color: '#e5e7eb', fontSize: '0.85rem', padding: '0 16px' };
const zoomBar: React.CSSProperties = { position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(0,0,0,0.35)', padding: 6, borderRadius: 9999, zIndex: 2 };

export default MenuImagesModal;
